import React, {useCallback, useEffect, useState} from 'react'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {useLocation, useParams} from 'react-router-dom'
import {useAsync, useAsyncRetry} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {PaymentInformation} from '../../Components/RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'
import {PAYMENT} from '../../../../Common/Constants'
import {PaymentMethod} from '../../../DTO/PaymentMethodDTO'
import {ContractInputInfo, ContractInputInfoAll, ContractTerm, ErrorMessageForInvalidCard, ErrorMessagesForContractEdit, findAmountByTermAndMethod, findSecurityCodeByCardIdForValidation, getDefaultSecurityCodeMapForMyCreditCard, makeStringCreditCardExpireDate, MyCreditCardInfo, UsingMyCreditCardInfo} from '../../../Props/ContractInputInfo'
import {BucketInfo} from '../../../DTO/BucketInfoDTO'
import {NextContractInfo} from '../../../DTO/MemberContractInfoDTO'
import {validateMemberContractInput, ValidationContractInput} from '../../../Utils/Validations/ValidateMembersContractInput'
import {animateScroll} from 'react-scroll/modules'
import {LeavingMessageContent} from '../../Components/LeavingMessageContent'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {ProcessingErrorPage} from '../Error/ProcessingErrorPage'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {MBLoadingOverlay} from '../../Molecules/MBLoadingOverlay'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {MBDeleteCreditCardConfirmDialog} from '../../Molecules/MBDialogs'
import {Nullable} from '../../../../Common/TypeHelper'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {checkAsyncStateAndGetDataOrError, isError, memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {ChangeNextContractTermContent} from './Components/ChangeNextContractTermContent'
import {ChangeNextPaymentMethodContent} from './Components/ChangeNextPaymentMethodContent'
import {MembersMobileCarrierMaintenancePage} from '../Error/MembersMobileCarrierMaintenancePage'
import {ErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/ErrorSummary'
import {Contract} from '../../../Utils/Resources'
import STATUS = Contract.STATUS

const pageTitle = 'お支払いに関する情報変更'

export type MembersEditContractInputPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

type EditContractInputPageLocationState = {
  isInvalidCardError: boolean
  isMobileCarrierError: boolean
  bucketInfo: BucketInfo
  paymentMethodList: PaymentMethod[]
  contractInputInfo: ContractInputInfo
  myCreditCardInfoList: UsingMyCreditCardInfo[]
  nextContractInfo: NextContractInfo
}

export const MembersEditContractInputPage:
  React.VFC<MembersEditContractInputPageProps> = (props) => {
  const {bucketKey} = useParams<{ bucketKey: string }>()
  const [processing, setProcessing] = useState<boolean>(false)

  const location = useLocation<EditContractInputPageLocationState>()
  const locationState: EditContractInputPageLocationState = location.state || {}

  const [openDialog, setOpenDialog] = useState(false)
  const [deleteCardInfo, setDeleteCardInfo] = useState<MyCreditCardInfo>()
  const [loadingOpen, setLoadingOpen] = useState(false)

  const [bucketInfo, setBucketInfo]
    = useState<BucketInfo>(locationState?.bucketInfo || {} as BucketInfo)

  const [paymentMethodList, setPaymentMethodList] =
    useState<PaymentMethod[]>(locationState?.paymentMethodList || [])
  const [myCreditCardInfoList, setMyCreditCardInfoList] =
    useState<UsingMyCreditCardInfo[]>(locationState?.myCreditCardInfoList || [])
  const [contractInputInfoState, setContractInputInfoState] = useState<ContractInputInfoAll>({
    term: locationState?.contractInputInfo?.term || '',
    method: locationState?.contractInputInfo?.method,
    amount: findAmountByTermAndMethod(
      locationState?.paymentMethodList,
      locationState?.contractInputInfo?.term,
      locationState?.contractInputInfo?.method,
    ),
    creditCard: {
      creditCardType: locationState?.contractInputInfo?.creditCard?.creditCardType || '',
      newCreditCardInfo: {
        cardNumber:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.cardNumber || '',
        expireMonth:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.expireMonth || '',
        expireYear:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.expireYear || '',
        holderName:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.holderName || '',
        securityCode:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.securityCode || '',
      },
      myCreditCardInfo: {
        selectedCardId:
          locationState?.contractInputInfo?.creditCard?.myCreditCardInfo.selectedCardId || '',
        securityCodeMap:
          locationState?.contractInputInfo?.creditCard?.myCreditCardInfo.securityCodeMap || [],
      },
    },
    mobileCarrier: {
      carrierKey: locationState?.contractInputInfo?.mobileCarrier?.carrierKey || '',
    },
    convenienceStore: {
      convenienceStoreKey:
        locationState?.contractInputInfo?.convenienceStore?.convenienceStoreKey || '',
    },
  })
  const [errorState, setErrorState] = useState<ErrorMessagesForContractEdit>({
    isInputError: locationState.isInvalidCardError || false,
    invalidCardErrors:
      ErrorMessageForInvalidCard(
        locationState?.contractInputInfo?.creditCard?.creditCardType,
        locationState?.isInvalidCardError,
      ),
    paymentInputTermErrors: [],
    paymentInputMethodErrors: [],
    creditCardTypeErrors: [],
    creditCardNumberErrors: [],
    creditCardExpireMonthErrors: [],
    creditCardExpireYearErrors: [],
    creditCardFutureExpireDateErrors: [],
    creditCardHolderNameErrors: [],
    creditCardSecurityCodeErrors: [],
    errorMyCreditCardId: (locationState.isInvalidCardError
      && locationState.contractInputInfo.creditCard.creditCardType === 'myCard'
    )
      ? locationState?.contractInputInfo?.creditCard?.myCreditCardInfo.selectedCardId
      : '',
    myCreditCardSecurityCodeErrors: [],
    convenienceStoreErrors: [],
    mobileCarrierErrors: locationState?.isMobileCarrierError
      ? ['決済が完了しませんでした。再度お手続きを実施ください。']
      : [],
  })

  const throwMBMemberError = useMBMemberError()

  const resultGetSelfCreditCards = useAsyncRetry(async () =>
    await props.memberRepo.getSelfCreditCards())

  const handleDialogCancelButtonClick = useCallback((): void => {
    setOpenDialog(false)
  }, [openDialog])

  const handleDeleteCardButtonClick = useCallback((deleteCardInfo: MyCreditCardInfo): void => {
    setOpenDialog(true)
    setDeleteCardInfo(deleteCardInfo)
  }, [openDialog, deleteCardInfo])

  const handleDialogDeleteCardButtonClick = useCallback(async (cardId: string): Promise<void> => {
    if (next === null) {
      throw new MBMemberError('SystemError')
    }

    setOpenDialog(false)
    setLoadingOpen(true)
    props.navigator.changeEditContractInputPageState(
      bucketInfo,
      paymentMethodList,
      contractInputInfoState,
      myCreditCardInfoList,
      next,
    )

    try {
      const deleteCardResult = await props.memberRepo.deleteCreditCard(cardId)
      switch (deleteCardResult.status) {
        case CommonResponseStatus.Ok:
        case CommonResponseStatus.NotFound:
          setContractInputInfoState((prevState) => ({
            ...prevState,
            method: 'credit_card',
            creditCard: {
              ...prevState.creditCard,
              creditCardType: '',
              myCreditCardInfo: {
                selectedCardId: '',
                securityCodeMap: prevState.creditCard.myCreditCardInfo.securityCodeMap,
              },
            },
          }))
          resultGetSelfCreditCards.retry()
          break
        case CommonResponseStatus.BadRequest:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        case CommonResponseStatus.InvalidRequest:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
      }

      setLoadingOpen(false)
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }, [
    props.navigator,
    props.memberRepo,
    resultGetSelfCreditCards,
  ])

  const resultBucketInfo = useAsync(async () =>
    await props.memberRepo.getBucketInfoByBucketKey(bucketKey))
  const resultContractInfo = useAsync(async () =>
    await props.memberRepo.getContractInfoByBucketKey(bucketKey))

  useEffect(() => {
    if (!resultGetSelfCreditCards.value) {
      return
    }
    setMyCreditCardInfoList(resultGetSelfCreditCards.value.data)

    if (
      locationState?.bucketInfo &&
      locationState?.paymentMethodList &&
      locationState?.myCreditCardInfoList &&
      locationState?.contractInputInfo &&
      locationState?.nextContractInfo
    ) {
      return
    }

    if (!resultBucketInfo.value) {
      return
    }
    if (!resultContractInfo.value) {
      return
    }
    if (!resultContractInfo?.value?.data?.candidatePaymentMethodList) {
      return
    }
    const bucketInfoDTO = resultBucketInfo.value.data
    const {candidatePaymentMethodList} = resultContractInfo.value.data

    setBucketInfo((prevState) => ({
      ...prevState,
      ...bucketInfoDTO,
    }))

    setPaymentMethodList([...candidatePaymentMethodList])

  }, [
    resultGetSelfCreditCards.value,
    resultBucketInfo.value,
    resultContractInfo.value,
  ])

  useEffect(() => {
    if (locationState?.contractInputInfo) {
      return
    }
    if (!resultContractInfo.value?.data) {
      return
    }

    setContractInputInfoState((prevState) => ({
      ...prevState,
      term: locationState?.nextContractInfo?.term
        || !isContractTerms(paymentMethodList) && resultContractInfo?.value?.data?.next?.term
        || '',
      creditCard: {
        creditCardType: '',
        newCreditCardInfo: {
          cardNumber: '',
          expireMonth: '',
          expireYear: '',
          holderName: '',
          securityCode: '',
        },
        myCreditCardInfo: {
          selectedCardId: '',
          securityCodeMap: getDefaultSecurityCodeMapForMyCreditCard(myCreditCardInfoList),
        },
      },
    }))
  }, [
    locationState?.contractInputInfo,
    resultContractInfo,
    myCreditCardInfoList,
    paymentMethodList,
  ])

  if (resultBucketInfo.loading) {
    return null
  }

  if (resultBucketInfo.error) {
    throw new MBMemberError(resultBucketInfo.error.message)
  }

  if (!resultBucketInfo.value) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(resultBucketInfo.value.status)

  if (!resultBucketInfo.value.data) {
    throw new MBMemberError('UnknownError')
  }

  if (resultGetSelfCreditCards.loading) {
    return null
  }

  if (resultGetSelfCreditCards.error) {
    throw new MBMemberError(resultGetSelfCreditCards.error.message)
  }

  if (!resultGetSelfCreditCards.value) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(resultGetSelfCreditCards.value.status)

  if (resultContractInfo.loading) {
    return null
  }

  const contractInfo = checkAsyncStateAndGetDataOrError(resultContractInfo)
  if (isError(contractInfo)) {
    if (contractInfo.message === 'Processing') {
      return <ProcessingErrorPage
        pageTitle={pageTitle}
        bucketInfo={resultBucketInfo.value.data}
        buttonName={resultBucketInfo.value.data.fcMyPageInfo.name}
        url={resultBucketInfo.value.data.fcMyPageInfo.url}
      />
    } else {
      throw contractInfo
    }
  }

  const {now, next} = contractInfo

  if (now.contractMethod.method === 'none') {
    throw new MBMemberError('NotFound')
  }
  if (next === null) {
    throw new MBMemberError('SystemError')
  }
  if (next.status === STATUS.Canceled) {
    return (
      <LeavingMessageContent
        pageTitle={pageTitle}
        bucketInfo={bucketInfo}
      />
    )
  }
  if (!(next.status === STATUS.Reserved)) {
    throw new MBMemberError('SystemError')
  }
  if (next.contractMethod.method === 'mobile_carrier'
    && next.contractMethod.paymentMethodDetail.isUnableToCancelContract) {
    const bucketInfo = resultBucketInfo.value.data
    return (
      <MembersMobileCarrierMaintenancePage
        pageTitle={pageTitle}
        operationName={'お支払いに関する情報変更手続き'}
        bucketInfo={bucketInfo}
        buttonName={bucketInfo.fcMyPageInfo.name}
        url={bucketInfo.fcMyPageInfo.url}
      />
    )
  }

  const currentTerm: ContractTerm = locationState?.nextContractInfo?.term || next.term || ''
  const getCreditCardInfoForNextContract = (): Nullable<MyCreditCardInfo> => {
    if (locationState?.nextContractInfo?.contractMethod.method === 'credit_card') {
      return locationState?.nextContractInfo?.contractMethod.paymentMethodDetail
    }
    if (next.contractMethod.method === 'credit_card') {
      return next.contractMethod.paymentMethodDetail
    }
    return null
  }
  const creditCardInfoForNextContract = getCreditCardInfoForNextContract()

  if (paymentMethodList.length === 0) {
    return null
  }
  const paymentInformationList: PaymentInformation[] =
    paymentMethodList
      .filter(paymentMethod =>
        Object.keys(PAYMENT.TERMS).includes(paymentMethod.term),
      )
      .map(paymentMethod => ({
        isFreeBucket: paymentMethod.method === 'none',
        termName: PAYMENT.TERMS[paymentMethod.term].displayLabel,
        contractAmount: paymentMethod.amount,
        order: PAYMENT.TERMS[paymentMethod.term].order,
        term: paymentMethod.term,
      }))

  if (paymentInformationList.length === 0) {
    throw new MBMemberError('SystemError')
  }

  const goToEditContractInfoPage = (): void => {
    props.navigator.goToEditContractInfoPage(bucketKey)
  }

  const goToEditContractConfirmPage = (): void => {
    setProcessing(true)

    const inputForValidation: ValidationContractInput = {
      paymentInputTerm: contractInputInfoState.term,
      paymentInputMethod: contractInputInfoState.method,
      creditCardType: contractInputInfoState.creditCard.creditCardType,
      creditCardNumber:
        contractInputInfoState?.creditCard?.newCreditCardInfo.cardNumber || '',
      creditCardExpireMonth:
        contractInputInfoState?.creditCard?.newCreditCardInfo.expireMonth || '',
      creditCardExpireYear:
        contractInputInfoState?.creditCard?.newCreditCardInfo.expireYear || '',
      creditCardHolderName:
        contractInputInfoState?.creditCard?.newCreditCardInfo.holderName || '',
      creditCardSecurityCode:
        contractInputInfoState?.creditCard?.newCreditCardInfo.securityCode || '',
      myCreditCardSecurityCode:
        findSecurityCodeByCardIdForValidation(contractInputInfoState.creditCard.myCreditCardInfo)
        || '',
      convenienceStore: contractInputInfoState?.convenienceStore?.convenienceStoreKey || '',
      mobileCarrier: contractInputInfoState?.mobileCarrier?.carrierKey || '',
      nextContractStartDate: next.startDate,
    }

    const validationResult = validateMemberContractInput(inputForValidation)

    if (validationResult.hasError) {
      setErrorState({
        isInputError: true,
        invalidCardErrors: [],
        paymentInputTermErrors: validationResult.errors.paymentInputTerm,
        paymentInputMethodErrors: validationResult.errors.paymentInputMethod,
        errorMyCreditCardId:
          contractInputInfoState.creditCard.creditCardType === 'myCard'
            ? contractInputInfoState.creditCard.myCreditCardInfo.selectedCardId
            : '',
        creditCardTypeErrors: validationResult.errors.creditCardType,
        creditCardNumberErrors: validationResult.errors.creditCardNumber,
        creditCardExpireMonthErrors: validationResult.errors.creditCardExpireMonth,
        creditCardExpireYearErrors: validationResult.errors.creditCardExpireYear,
        creditCardFutureExpireDateErrors: validationResult.errors.creditCardFutureExpireDate,
        creditCardHolderNameErrors: validationResult.errors.creditCardHolderName,
        creditCardSecurityCodeErrors: validationResult.errors.creditCardSecurityCode,
        myCreditCardSecurityCodeErrors: validationResult.errors.myCreditCardSecurityCode,
        convenienceStoreErrors: [],
        mobileCarrierErrors: validationResult.errors.mobileCarrier,
      })
      setProcessing(false)
      animateScroll.scrollToTop()
      return
    }

    if (next.contractMethod.method === 'mobile_carrier') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {isUnableToCancelContract, ...nextRest} = next.contractMethod.paymentMethodDetail

      props.navigator.goToEditContractConfirmPage(
        bucketInfo,
        paymentMethodList,
        {
          ...contractInputInfoState, amount: findAmountByTermAndMethod(
            paymentMethodList,
            contractInputInfoState.term,
            contractInputInfoState.method,
          ),
        },
        myCreditCardInfoList,
        {
          ...next,
          contractMethod: {
            method: 'mobile_carrier',
            paymentMethodDetail: nextRest,
          },
        },
      )
      return
    }

    props.navigator.goToEditContractConfirmPage(
      bucketInfo,
      paymentMethodList,
      {
        ...contractInputInfoState, amount: findAmountByTermAndMethod(
          paymentMethodList,
          contractInputInfoState.term,
          contractInputInfoState.method,
        ),
      },
      myCreditCardInfoList,
      next,
    )
    return
  }

  return (
    <>
      <MembersPageTemplate
        hideFooterNavi={true}
        bucketInfo={bucketInfo}
      >
        <PageTitle>{pageTitle}</PageTitle>

        <InputErrorSummary display={errorState.isInputError}/>
        <ErrorSummary
          display={!errorState.isInputError && locationState.isMobileCarrierError}
          headline={'キャリア決済に失敗しました。'}
          detail={
            '入力項目をご確認の上、再実施いただくか、他の決済方法をご利用ください。\n また、何度もこのメッセージが表示される場合には、各キャリアへ問い合わせをお願いします。'
          }
        />

        <StepGuide activity={'edit'} current={'enter'}/>

        <PIMStack alignItems={'center'} spacing={2}>
          <ChangeNextContractTermContent
            paymentInformationList={paymentInformationList}
            defaultValue={currentTerm}
            value={contractInputInfoState.term}
            onChange={(changeSelectedTerm: ContractTerm): void => {
              setContractInputInfoState((prevState) => {
                return {
                  ...prevState,
                  term: changeSelectedTerm,
                  method: undefined,
                  amount: 0,
                  creditCard: {
                    ...prevState.creditCard,
                    creditCardType: '',
                  },
                }
              })
            }}
            errorMessage={errorState.paymentInputTermErrors}
          />

          <ChangeNextPaymentMethodContent
            paymentMethodList={paymentMethodList}
            myCreditCardInfoList={myCreditCardInfoList}
            contractInputInfoState={contractInputInfoState}
            displayMyCreditCardExcludeCurrentCard={
              myCreditCardInfoList.length > 1 || paymentMethodList.length > 1
            }
            isChangeContractTerm={
              isChangeTerm(currentTerm, contractInputInfoState.term)
            }
            isDisableContractMethodRadioButton={isContractTerms(paymentMethodList)}
            nextContractStartDate_YYYYMMDD={
              locationState?.nextContractInfo?.startDate || next.startDate
            }
            creditCardInfoForNextContract={creditCardInfoForNextContract}
            onChangeSelectedMethod={(changeSelectedMethod): void => {
              setContractInputInfoState((prevState) => {
                return {
                  ...prevState,
                  method: changeSelectedMethod,
                  amount: findAmountByTermAndMethod(
                    paymentMethodList, prevState.term, changeSelectedMethod,
                  ),
                }
              })
            }}
            onChangeInputDetailInfo={(changeInputDetailInfo): void => {
              setContractInputInfoState((prevState) => {
                return {
                  ...prevState,
                  creditCard: changeInputDetailInfo.creditCard,
                  mobileCarrier: changeInputDetailInfo.mobileCarrier,
                  convenienceStore: changeInputDetailInfo.convenienceStore,
                }
              })
            }}
            errorMessages={errorState}
            onDeleteCardButtonClick={handleDeleteCardButtonClick}
            nextContractInfoBeforeChange={next}
            nowContractInfo={now}
          />

        </PIMStack>

        <MessageForAction>上記の内容に間違いがなければ<br/>『変更内容の確認に進む』ボタンを押してください。</MessageForAction>

        <PIMPageActions>
          <PIMSubmitButton
            testId={'confirm-changes-button'}
            disabled={processing}
            onClick={(): void => {
              goToEditContractConfirmPage()
            }}
          >
            変更内容の確認に進む
          </PIMSubmitButton>
          <PIMSecondaryButton onClick={goToEditContractInfoPage}>
            契約情報に戻る
          </PIMSecondaryButton>
        </PIMPageActions>

      </MembersPageTemplate>

      <MBLoadingOverlay open={loadingOpen}/>
      {
        deleteCardInfo &&
        <MBDeleteCreditCardConfirmDialog
          open={openDialog}
          creditCardInfo={
            deleteCardInfo.cardNumber + makeStringCreditCardExpireDate(deleteCardInfo.expireDate)
          }
          onCancelButtonClick={handleDialogCancelButtonClick}
          onOkButtonClick={(): Promise<void> =>
            handleDialogDeleteCardButtonClick(deleteCardInfo.cardId)
          }
        />
      }
    </>
  )
}

const isContractTerms = (paymentMethodList: PaymentMethod[]): boolean => {
  return Array.from(new Set(
    paymentMethodList.map(payMethod => payMethod.term),
  )).length > 1
}

const isChangeTerm = (currentTerm: string, inputTerm: string): boolean => {

  return (currentTerm === inputTerm)
}

